import { FC, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {ActionIcon, Alert, Anchor, Box, Button, Checkbox, Drawer, Flex, Loader, rem, Table, Text, Tooltip} from "@mantine/core";
import styles from '@/PageComponents/SectionTable/SectionTable.module.css';
import {
    closestCorners,
    DndContext, DragEndEvent,
    KeyboardSensor,
    PointerSensor, useDroppable,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import {ColumnMappingData, TableAction, TableActionStates} from "@/PageComponents/Table/table-model";
import { IconCheck, IconMinus, IconPlus } from "@tabler/icons";
import SectionTableSection from "@/PageComponents/SectionTable/SectionTableSection";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { IconChevronDown, IconDeviceFloppy} from "@tabler/icons-react";
import { SimpleColumnMapping } from "@/PageComponents/SimpleTable/SimpleTable";
import ScColumnMappingButton from "@/PageComponents/SectionTable/ScColumnMappingButton";
import { useStableCallback } from "@/hooks/useStableCallback";
import { getGroupedItems, sectionGroupKey, shareUnchangedGroups } from "@/PageComponents/SectionTable/section-table-data";

export type SimpleVal = number | string | { color?: null | string; value: null | string }
export interface SimpleData { [key: string]: SimpleVal | any }

export const getSectionsFromTableData = (data: any[], sectionNameKey: string, sectionIdKey: string, module: number, itemId: string) => {
    const sections: { [groupId: string]: any } = data.reduce((p: any, c: any, i: any, a: any) =>
        c[sectionIdKey] ? ({
            ...p,
            [c[sectionIdKey]]: [{ ...c }]
        }) :
            ({
                ...p
            })
        , {})

    return Object.entries(sections)
        .map(([k, x]: any) => {
            return ({
                ID: x[0][sectionIdKey],
                Name: x[0][sectionNameKey] || null,
                IsActive: true,
                FromBundleID: x[0]?.FromBundleID || null,
                HideLineItems: x[0]?.HideLineItems || false,
                DisplaySubtotal: x[0]?.DisplaySubtotal || false,
                Module: module,
                ItemID: itemId,
            })
        })
}

export const getDefaultSectionObject = (id: string, module: number, itemId: string, providedName?: string, bestPredictedDefaultPdfSettingValues?: { HideLineItems: boolean; DisplaySubtotal: boolean; }) => {
    return {
        ID: id,
        IsActive: true,
        Name: providedName || '',
        FromBundleID: null,
        HideLineItems: bestPredictedDefaultPdfSettingValues?.HideLineItems ?? false,
        DisplaySubtotal: bestPredictedDefaultPdfSettingValues?.DisplaySubtotal ?? false,
        Module: module,
        ItemID: itemId,
    }
}

export { getGroupedItems };

// sections start folded on documents with more lines than this, so large documents open quickly
const foldSectionsAboveLines = 50

// module-level so DndContext props stay referentially stable: a new sensor options object or modifiers array
// changes the dnd context value, which re-renders every draggable section regardless of memo
const keyboardSensorOptions = { coordinateGetter: sortableKeyboardCoordinates }
const dndModifiers = [restrictToVerticalAxis, restrictToParentElement]

// expand all opens sections in batches of about this many rows, so the first sections show at once and the page
// stays responsive on big quotes instead of freezing while every row mounts
const expandAllRowsPerBatch = 20

const SectionTable: FC<{
    allowSections?: boolean
    data: SimpleData[]
    mapping: SimpleColumnMapping[]
    showTotals?: boolean
    footerRow?: (string | number)[]
    height?: any
    width?: any
    minHeight?: any
    onItemClicked?: (item: any, column: string) => void
    // onReorder?: (newItems: any[]) => void
    // on data update is called in real time to manipulate the data as needed when making changes
    onDataUpdate: (newItems: any[]) => void
    // onDebouncedDataUpdated will reflect the latest changes after a set interval if changes was made - used to debounce interactions to the server
    // onDebouncedDataUpdated?: (data: any[], sections: any[]) => void
    // debounceTimeout?: number
    // onReorderIndex?: (event: any, previousIndex: number, nextIndex: number) => void
    uniqueIdKey?: string
    onAction?: (actionName: string, actionItem: any, actionItemIndex: number, group: any) => void
    canEdit?: boolean
    controls?: TableAction[]
    sectionControls: any[]
    sectionTitleKey: string;
    sectionIdKey: string;
    // sectionData: any[];
    stylingProps?: {
        compact?: boolean
        rows?: boolean
        darkerText?: boolean
        clearHeader?: boolean
    }
    onInputChange?: (name: string, item: any, value: number | '') => void
    addButton?: {
        label?: string
        callback?: () => void
        customComponent?: ReactNode
    }
    tableActionStates?: TableActionStates
    showControlsOnHover?: boolean
    // specify input props for specific items
    tableItemInputMetadataByKeyName?: { [itemID: string]: { [itemValueKeyName: string]: { disabled: boolean; loading: boolean; error?: string | null } } }
    module: number
    itemId: string
    onSectionItem: (actionName: string, group: any) => void
    savingItem?: boolean
    history?: [data: any[], sections: any[]][]
    onUndo?: () => void
    useHideLineItemsPdfSetting?: boolean,
    useDisplaySubtotalsPdfSetting?: boolean,
    // when specified, normal drag controls will become checkboxes for selection
    itemsSelected?: (items: any[]) => void
    itemsSelectedFilter?: (item: any) => boolean
    // onConfirmInputUpdate?: (item: any) => void
    headingSubtotalValueFunction?: (itemsInSection: any[]) => number | undefined
    showNewBundleHint?: boolean
    onPredictedDefaultSectionPdfSettingsChanged?: (pdfSettings: { HideLineItems: boolean; DisplaySubtotal: boolean; }) => void
    lineClamp?: number
    columnConfigModelName?: string;
    userColumnConfig?: ColumnMappingData[]
    customChildren?: ReactNode;
    sectionHeaderDisplayValueFunction?: (group: any) => string | React.ReactNode
}> = (props: any) => {

    const [uKey] = useState(props.uniqueIdKey || 'ID')
    const [sectionNameKey] = useState(props.sectionTitleKey)
    const [sectionIdKey] = useState(props.sectionIdKey)

    // Determines if section total values should be calculating including/excluding VAT
    const useInclusiveValues = useMemo(() => {
        return props.userColumnConfig?.some((x: any) => x.ColumnName === 'LineTotalInclusive' && x.Show) || false
    }, [props.userColumnConfig])

    // const [collapsedSectionIDs, setCollapsedSectionIDs] = useState<string[]>([]);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, keyboardSensorOptions)
    );

    // add group id to data to avoid assigning new gid to items when reordering
    /*const dataWithGroupIds = useMemo(() => (
        props.data.map((x, i) => (
            // !x.SectionID ? {...x, groupId: crypto?.randomUUID() || 'sectionForItem' + i + x.ID} : {...x}
            { ...x, groupId: x[sectionIdKey] || crypto?.randomUUID() || 'sectionForItem' + i + x.ID }
        )).sort((a: any, b: any) => (a.LineNumber > b.LineNumber ? 1 : a.LineNumber === b.LineNumber ? 0 : -1))
    ), [props.data])*/
    const [orderedData, setOrderedData] = useState(() =>
        [...props.data].sort((a: any, b: any) => (a.LineNumber > b.LineNumber ? 1 : a.LineNumber === b.LineNumber ? 0 : -1))
    )
    const groupKeyOf = useMemo(() => sectionGroupKey(sectionIdKey, uKey), [sectionIdKey, uKey])
    const [groupedData, setGroupedData] = useState(() => getGroupedItems(orderedData, sectionNameKey, sectionIdKey, useInclusiveValues, groupKeyOf))
    const [sectionData, setSectionData] = useState(() =>
        groupedData.filter((x: any) => x.isSection).map((x: any) => ({
            ID: x.id,
            IsActive: true,
            Name: x.name,
            FromBundleID: x.fromBundleId,
            HideLineItems: x.hideLineItems,
            DisplaySubtotal: x.displaySubtotal,
            Module: props.module,
            ItemID: props.itemId,
        }))
    )

    const [preferredSectionCollapsedState, setPreferredSectionCollapsedState] = useState(() => orderedData.length > foldSectionsAboveLines)

    // section id -> expanded. A section first seen takes the preferred state of that moment and keeps it, matching
    // the old per-section useState, so crossing the collapse threshold later doesn't fold sections the user opened
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({})
    const initialExpandedRef = useRef<Record<string, boolean>>({})
    groupedData.forEach((group: any) => {
        if (group.isSection && !(group.id in initialExpandedRef.current)) {
            initialExpandedRef.current[group.id] = !preferredSectionCollapsedState
        }
    })
    const isSectionExpanded = (id: string) => expandedSections[id] ?? initialExpandedRef.current[id] ?? false
    const sectionGroups = groupedData.filter((x: any) => x.isSection)
    const allSectionsExpanded = sectionGroups.every((x: any) => isSectionExpanded(x.id))

    // batches of section ids still to expand; the next batch is queued only after the previous one has committed,
    // otherwise queued timers fire before React renders and the batches merge back into one long freeze
    const [expandAllQueue, setExpandAllQueue] = useState<string[][]>([])
    const expandingAll = expandAllQueue.length !== 0
    const stopExpandingAll = () => setExpandAllQueue(prev => prev.length === 0 ? prev : [])
    useEffect(() => {
        if (expandAllQueue.length === 0) return
        const timeout = setTimeout(() => {
            setSectionsExpanded(expandAllQueue[0], true)
            setExpandAllQueue(expandAllQueue.slice(1))
        }, 0)
        return () => clearTimeout(timeout)
    }, [expandAllQueue]);

    const setSectionsExpanded = (ids: string[], expanded: boolean) =>
        setExpandedSections(prev => ({ ...prev, ...Object.fromEntries(ids.map(id => [id, expanded])) }))

    const handleSetSectionExpanded = (id: string, expanded: boolean) => {
        stopExpandingAll()
        setSectionsExpanded([id], expanded)
    }

    const handleToggleAllSections = () => {
        stopExpandingAll()
        if (allSectionsExpanded) {
            setSectionsExpanded(sectionGroups.map((x: any) => x.id), false)
            return
        }

        const batches: string[][] = []
        let rows = Infinity
        sectionGroups.filter((x: any) => !isSectionExpanded(x.id)).forEach((group: any) => {
            if (rows >= expandAllRowsPerBatch) {
                batches.push([])
                rows = 0
            }
            batches[batches.length - 1].push(group.id)
            rows += group.items.length
        })

        if (batches.length === 0) return
        setSectionsExpanded(batches[0], true)
        setExpandAllQueue(batches.slice(1))
    }

    const dataChangedInternally = useRef(false)

    useEffect(() => {
        if (!dataChangedInternally.current) {
            const orderedDataWithUid = [...props.data].sort((a: any, b: any) => (a.LineNumber > b.LineNumber ? 1 : a.LineNumber === b.LineNumber ? 0 : -1))
            setOrderedData(orderedDataWithUid)
            const groupedData = getGroupedItems(orderedDataWithUid, sectionNameKey, sectionIdKey, useInclusiveValues, groupKeyOf)
            setGroupedData(prev => shareUnchangedGroups(prev, groupedData))
            const sectionData = groupedData.filter((x: any) => x.isSection).map((x: any) => ({
                ID: x.id,
                IsActive: true,
                Name: x.name,
                FromBundleID: x.fromBundleId,
                HideLineItems: x.hideLineItems,
                DisplaySubtotal: x.displaySubtotal,
                Module: props.module,
                ItemID: props.itemId,
            }))
            setSectionData(sectionData)
            setPreferredSectionCollapsedState(orderedDataWithUid.length > foldSectionsAboveLines)
        }
        dataChangedInternally.current = false
    }, [props.data, useInclusiveValues]);

    /*const groupedData = useMemo(() => {
        const groupedBySectionID: { [groupId: string]: any } = orderedData.reduce((p, c, i, a) =>
            p.hasOwnProperty(c.groupId) ? ({ ...p, [c.groupId]: [...p[c.groupId], { ...c }] }) :
                ({
                    ...p,
                    [c.groupId]: [{ ...c }]
                })
            , {})
        return Object.entries(groupedBySectionID).sort(([, [a]], [, [b]]) => a.LineNumber > b.LineNumber ? 1 : -1)
            .map(([k, x]) => {
                return ({
                    id: x[0].groupId,
                    name: x[0][sectionNameKey] || null,
                    items: x,
                    isSection: !!x[0][sectionIdKey],
                    hideLineItems: x[0]?.HideLineItems || false,
                    displaySubtotal: x[0]?.DisplaySubtotal || false,
                    fromBundleId: x[0]?.FromBundleID || null,
                })
            })
    }, [orderedData])*/

    /*const sectionData = useMemo(() => {
        return groupedData.filter(x => x.isSection).map((x) => ({
            ID: x.id,
            IsActive: true,
            Name: x.name,
            FromBundleID: x.fromBundleId,
            HideLineItems: x.hideLineItems,
            DisplaySubtotal: x.displaySubtotal,
            Module: props.module,
            ItemID: props.itemId,
        }))
    }, [groupedData])*/

    // const [debouncedData, setDebouncedData] = useDebouncedState<[data: any[], sections: any[]]>([props.data, sectionData], props.debounceTimeout || 500)


    const handleUpdate = (data: any, sections?: any) => {
        const orderedDataWithUid = [...data].sort((a: any, b: any) => (a.LineNumber > b.LineNumber ? 1 : a.LineNumber === b.LineNumber ? 0 : -1))
        setOrderedData(orderedDataWithUid)
        const groupedData = getGroupedItems(orderedDataWithUid, sectionNameKey, sectionIdKey, useInclusiveValues, groupKeyOf)
        setGroupedData(prev => shareUnchangedGroups(prev, groupedData))
        const sectionData = groupedData.filter((x: any) => x.isSection).map((x: any) => ({
            ID: x.id,
            IsActive: true,
            Name: x.name,
            FromBundleID: x.fromBundleId,
            HideLineItems: x.hideLineItems,
            DisplaySubtotal: x.displaySubtotal,
            Module: props.module,
            ItemID: props.itemId,
        }))
        setSectionData(sectionData)
        setPreferredSectionCollapsedState(orderedDataWithUid.length > foldSectionsAboveLines)
        if (!!props.onDataUpdate) {
            dataChangedInternally.current = true
            props.onDataUpdate(data)
        }
    }

    function handleDragStart(event: any) {
    }

    // loose lines have a synthetic 'line-<ID>' group id: dropping into one starts a new section with a real GUID
    const sectionIdForGroup = (group: any) => group && !group.isSection ? crypto.randomUUID() : group?.id

    function handleDragEnd(event: DragEndEvent) {
        const { active, over } = event;

        if (active && over && active.id !== over.id) {

            let oldIndex = -1 // orderedData.findIndex(x => x[uKey] === active.id || x[uKey] === active.data.current.itemId);

            let sectionName: string | null = ''
            let sectionId: string | null = ''
            let displaySubtotal = false
            let hideLineItems = false
            let fromBundleId: string | null = null

            let transferringItems: any[] = []

            let itemId = over.data.current?.itemId || over.data.current?.group?.items[0]?.[uKey]

            if (/*!!over.data.current?.itemId*/ over.data.current?.type === 'group-item' || over.data.current?.type === 'row-above' || over.data.current?.type === 'row-below') { // dragged into group
                sectionName = groupedData[over.data.current?.groupIndex]?.name
                sectionId = sectionIdForGroup(groupedData[over.data.current?.groupIndex])
                displaySubtotal = groupedData[over.data.current?.groupIndex]?.displaySubtotal
                hideLineItems = groupedData[over.data.current?.groupIndex]?.hideLineItems
                fromBundleId = groupedData[over.data.current?.groupIndex]?.fromBundleId

            } else if (over.data.current?.type === 'group-above' || over.data.current?.type === 'group-below') { // dragged above or below another section
                // retain current active item group data
                const item = active.data.current?.itemId && orderedData.find((x: any) => x.ID === active.data.current?.itemId)

                sectionName = active.data.current?.itemId ? null : groupedData[active.data.current?.groupIndex]?.name
                displaySubtotal = active.data.current?.itemId ? item.DisplaySubtotal : groupedData[active.data.current?.groupIndex]?.displaySubtotal
                hideLineItems = active.data.current?.itemId ? item.HideLineItems : groupedData[active.data.current?.groupIndex]?.hideLineItems
                fromBundleId = active.data.current?.itemId ? null : groupedData[active.data.current?.groupIndex]?.fromBundleId
                sectionId = active.data.current?.itemId ? null : active.data.current?.group ? (active.data.current?.group.items[0][sectionIdKey] || null) : null


                itemId = over.data.current?.type === 'group-above' ? groupedData[over.data.current?.groupIndex].items[0][uKey] : orderedData[orderedData.length - 1][uKey]
            } else if (active.data.current?.itemId) {
                // active item is row item: group must match dropped group or new group must be created
                sectionName = (!!over.data.current?.itemId || over.data.current?.type === 'row-above' || over.data.current?.type === 'row-below') ? groupedData[over.data.current?.groupIndex]?.name : null
                sectionId = (!!over.data.current?.itemId || over.data.current?.type === 'row-above' || over.data.current?.type === 'row-below') ? sectionIdForGroup(groupedData[over.data.current?.groupIndex]) : null
                displaySubtotal = (!!over.data.current?.itemId || over.data.current?.type === 'row-above' || over.data.current?.type === 'row-below') ? groupedData[over.data.current?.groupIndex]?.displaySubtotal : false
                hideLineItems = (!!over.data.current?.itemId || over.data.current?.type === 'row-above' || over.data.current?.type === 'row-below') ? groupedData[over.data.current?.groupIndex]?.hideLineItems : false
                fromBundleId = (!!over.data.current?.itemId || over.data.current?.type === 'row-above' || over.data.current?.type === 'row-below') ? groupedData[over.data.current?.groupIndex]?.fromBundleId : null
            }

            /* else if(over.data.current?.type === 'group-item') { // merging groups

            }*/


            if (active.data.current?.type === 'group-item') { // dragging group item
                const overItemId = active.data.current?.itemId || active.data.current?.group.items[0]?.[uKey]
                oldIndex = orderedData.findIndex((x: any) => x[uKey] === overItemId)
                transferringItems = active.data.current?.group.items.map((x: any) => (
                    {
                        ...x,
                        [sectionIdKey]: sectionId,
                        [sectionNameKey]: sectionName,
                        HideLineItems: hideLineItems,
                        DisplaySubtotal: displaySubtotal,
                        FromBundleID: fromBundleId,
                    }
                ))
            } else if (active.data.current?.itemId) { // dragging line item
                oldIndex = orderedData.findIndex((x: any) => x[uKey] === active.data.current?.itemId)
                transferringItems = [{
                    ...orderedData.find((x: any) => x[uKey] === active.data.current?.itemId),
                    [sectionNameKey]: sectionName,
                    [sectionIdKey]: sectionId,
                    HideLineItems: hideLineItems,
                    DisplaySubtotal: displaySubtotal,
                    FromBundleID: fromBundleId,
                }]
            }

            const filtered = orderedData.filter((x: any) => !transferringItems.some((y: any) => x[uKey] === y[uKey]))
            const index = active.data.current?.itemId === itemId ? orderedData.findIndex((x: any) => x[uKey] === itemId) : filtered.findIndex((x: any) => x[uKey] === itemId) + (
                (over.data.current?.groupIndex === active.data.current?.groupIndex && over.data.current?.itemIndex > active.data.current?.itemIndex) ||
                    (over.data.current?.type === 'row-below' || over.data.current?.type === 'group-below') ? 1 : 0
            )

            const newItems = [
                ...filtered.slice(0, index),
                ...transferringItems,
                ...filtered.slice(index)
            ].map((x: any, i: any) => (
                { ...x, LineNumber: i + 1 }
            ))

            if (index !== -1) {
                // !!props.onReorder && props.onReorder(newItems)
                // !!props.onReorderIndex && props.onReorderIndex(event, oldIndex, index)
                handleUpdate(
                    newItems,
                )
            }


        }
    }
    /*
    
        const { setNodeRef } = useDroppable({
            id: 't-body',
        });
    */

    // a section the user just created opens with its title focused, even when large quotes start sections folded,
    // otherwise its only line disappears and it looks like an empty section
    const [newSectionId, setNewSectionId] = useState<string | null>(null)
    useEffect(() => {
        if (newSectionId) setNewSectionId(null)
    }, [newSectionId]);

    const handleCreateNewSection = (item: any) => {
        const sectionId = crypto.randomUUID()
        setSectionsExpanded([sectionId], true)
        setNewSectionId(sectionId)
        handleUpdate(
            orderedData.map((x: any) => x[uKey] === item[uKey] ? { ...x, [sectionIdKey]: sectionId, FromBundleID: null, ...bestPredictedDefaultPdfSettingValues } : x),
            // [...sectionData, getDefaultSectionObject(item.groupId, props.module, props.itemId, '', bestPredictedDefaultPdfSettingValues)]
        )
    }

    const handleClearSection = (group: any) => {
        handleUpdate(
            orderedData.map((x: any) => x[sectionIdKey] === group.id ? { ...x, [sectionIdKey]: null, [sectionNameKey]: null, FromBundleID: null } : x),
        )
    }

    const handleUpdateGroup = (group: any) => {
        handleUpdate(
            orderedData.map(
                (x: any) =>
                    x[sectionIdKey] === group.id ?
                        {
                            ...x,
                            [sectionNameKey]: group.name,
                            HideLineItems: group.hideLineItems,
                            DisplaySubtotal: group.displaySubtotal,
                        }
                        : x
            ),
            /*sectionData.map(x => (
                x.ID === group.id ? {
                    ...x,
                    Name: group.name,
                    HideLineItems: group.hideLineItems,
                    DisplaySubtotal: group.displaySubtotal,
                } : x
            ))*/
        )
    }

    const [selectedItems, setSelectedItems] = useState<any[]>([])

    const indeterminate = useMemo(() => {

        if (props.itemsSelectedFilter) {
            return props.itemsSelected && props.data && selectedItems.length !== props.data.filter(props.itemsSelectedFilter).length
                && props.data.filter(props.itemsSelectedFilter).some((x: any) => selectedItems.findIndex((y: any) => x.ID === y.ID) !== -1);
        }

        return props.itemsSelected && props.data && selectedItems.length !== props.data.length && props.data.some((x: any) => selectedItems.findIndex((y: any) => x.ID === y.ID) !== -1);
    }, [props.data, selectedItems])

    const checked = useMemo(() => {
        if (props.itemsSelectedFilter) {
            return props.itemsSelected && props.data && props.data.filter(props.itemsSelectedFilter).length !== 0
                && props.data.filter(props.itemsSelectedFilter).every((x: any) => selectedItems.findIndex((y: any) => x.ID === y.ID) !== -1);
        }

        return props.itemsSelected && props.data && props.data.length !== 0 && props.data.every((x: any) => selectedItems.findIndex((y: any) => x.ID === y.ID) !== -1);
    }, [props.data, selectedItems]);

    const onSelectAllItems = (e: any) => {

        const allData = (props.itemsSelectedFilter ? props.data?.filter(props.itemsSelectedFilter) : props.data) ?? [];

        setSelectedItems(e.currentTarget.checked ? allData : [])
        props.itemsSelected && props.itemsSelected(e.currentTarget.checked ? allData : [])
    }
    const handleItemsSelected = (items: any[]) => {

        if (props.itemsSelectedFilter) {
            items = items.filter(props.itemsSelectedFilter);
        }

        setSelectedItems(items)
        props.itemsSelected && props.itemsSelected(items)
    }

    const [globalHideLineItemsChecked, setGlobalHideLineItemsChecked] = useState(() => orderedData.length !== 0 && orderedData.filter((x: any) => !!x.InventorySectionID).every((x: any) => x.HideLineItems));

    const globalHideLineItemsIndeterminate = useMemo(() => {
        const filtered = orderedData.filter((x: any) => !!x.InventorySectionID)
        return (
            !filtered.every((x: any) => x.HideLineItems) && filtered.some((x: any) => x.HideLineItems)
        )
    }, [orderedData])

    const [globalDisplaySubtotalChecked, setGlobalDisplaySubtotalChecked] = useState(() => orderedData.length !== 0 && orderedData.filter((x: any) => !!x.InventorySectionID).every((x: any) => x.DisplaySubtotal))

    useEffect(() => {
        if (orderedData.length !== 0) {
            setGlobalHideLineItemsChecked(orderedData.filter((x: any) => !!x.InventorySectionID).every((x: any) => x.HideLineItems))
            setGlobalDisplaySubtotalChecked(orderedData.filter((x: any) => !!x.InventorySectionID).every((x: any) => x.DisplaySubtotal))
        }
    }, [orderedData]);

    const globalDisplaySubtotalIndeterminate = useMemo(() => {
        const filtered = orderedData.filter((x: any) => !!x.InventorySectionID)
        return (
            !filtered.every((x: any) => x.DisplaySubtotal) && filtered.some((x: any) => x.DisplaySubtotal)
        )
    }, [orderedData])

    const handleGlobalHideLineItemsChange = (checked: boolean) => {
        setGlobalHideLineItemsChecked(checked)
        handleUpdate(
            orderedData.map(
                (x: any) => ({
                    ...x,
                    HideLineItems: checked,
                })
            )/*,
            sectionData.map(x => ({
                ...x,
                HideLineItems: checked,
            }))*/
        );
    }

    const handleGlobalDisplaySubtotalChange = (checked: boolean) => {
        setGlobalDisplaySubtotalChecked(checked)
        handleUpdate(
            orderedData.map(
                (x: any) => ({
                    ...x,
                    DisplaySubtotal: checked,
                })
            )/*,
            sectionData.map(x => ({
                ...x,
                DisplaySubtotal: checked,
            }))*/
        );
    }

    const bestPredictedDefaultPdfSettingValues = useMemo(() => ({
        HideLineItems:
            globalHideLineItemsIndeterminate ? sectionData.filter((x: any) => x.HideLineItems) > sectionData.filter((x: any) => !x.HideLineItems) :
                globalHideLineItemsChecked,
        DisplaySubtotal:
            globalDisplaySubtotalIndeterminate ? sectionData.filter((x: any) => x.DisplaySubtotal) > sectionData.filter((x: any) => !x.DisplaySubtotal) :
                globalDisplaySubtotalChecked,
    }), [sectionData, globalHideLineItemsIndeterminate, globalHideLineItemsChecked, globalDisplaySubtotalIndeterminate, globalDisplaySubtotalChecked]);

    useEffect(() => {
        props.onPredictedDefaultSectionPdfSettingsChanged && props.onPredictedDefaultSectionPdfSettingsChanged(bestPredictedDefaultPdfSettingValues)
    }, [bestPredictedDefaultPdfSettingValues.HideLineItems, bestPredictedDefaultPdfSettingValues.DisplaySubtotal]);

    const [userColMappingConfig, setUserColMappingConfig] = useState<ColumnMappingData[]>(props.userColumnConfig ?? [])
    useEffect(() => {
        setUserColMappingConfig(props.userColumnConfig ?? [])
    }, [props.userColumnConfig]);

    const mapping = useMemo(() =>
        props.mapping
            .filter((x: any) => userColMappingConfig.length === 0 || (userColMappingConfig.find((y: any) => y.ColumnName === x.key)?.Show ?? true))
            .sort((a: any, b: any) => (userColMappingConfig.find((y: any) => y.ColumnName === a.key)?.Order || 0) - (userColMappingConfig.find((y: any) => y.ColumnName === b.key)?.Order || 0))
    , [userColMappingConfig, props.mapping])

    // Stable identities for the memoized sections and rows. Each calls the latest handler, so none goes stale.
    // Optional consumer callbacks stay undefined when not supplied: cells check e.g. `props.onItemClicked &&`.
    // Rows render from groupedData, which catches up with props.data one render after a change, so a quick
    // second edit can arrive with the previous copy of its item. Consumers that spread that item would undo the
    // first edit, so hand them the latest copy by ID.
    const stableOnInputChange = useStableCallback((name: string, item: any, value: any) =>
        props.onInputChange(name, props.data?.find((x: any) => x[uKey] === item?.[uKey]) ?? item, value)
    )
    const stableOnItemClicked = useStableCallback(props.onItemClicked)
    const stableOnAction = useStableCallback(props.onAction)
    const stableOnSectionItem = useStableCallback(props.onSectionItem)
    const onCreateNewSection = useStableCallback(handleCreateNewSection)
    const onUpdateGroup = useStableCallback(handleUpdateGroup)
    const onClearGroup = useStableCallback(handleClearSection)
    const onItemsSelected = useStableCallback(handleItemsSelected)
    const onDragStart = useStableCallback(handleDragStart)
    const onDragEnd = useStableCallback(handleDragEnd)
    const onSetSectionExpanded = useStableCallback(handleSetSectionExpanded)
    const onInputChange = props.onInputChange ? stableOnInputChange : undefined
    const onItemClicked = props.onItemClicked ? stableOnItemClicked : undefined
    const onAction = props.onAction ? stableOnAction : undefined
    const onSectionItem = props.onSectionItem ? stableOnSectionItem : undefined

    const tableHeadAndBody = (<>
        <Table.Thead>
            <Table.Tr>
                {
                    /*!!(props.onReorder || props.onReorderIndex) &&*/
                    <Table.Th
                        className={styles.actionCell}
                        maw={40}
                    >
                        {/*<span style={{position: "absolute", left: 10, bottom: 4}}>
                                {
                                    props.savingItem &&
                                    <span className={styles.savingFlicker}>
                                        <Tooltip label={'Saving Data...'} color={'scBlue'}>
                                            <IconDeviceFloppy color={'var(--mantine-color-scBlue-6)'} size={18}/>
                                        </Tooltip>
                                    </span>
                                }
                                </span>*/}
                        <Flex align={'center'}>
                        {
                            sectionGroups.length !== 0 &&
                            <Tooltip label={allSectionsExpanded ? 'Collapse all sections' : 'Expand all sections'} color={'scBlue'} openDelay={500}>
                                <ActionIcon
                                    variant={'transparent'}
                                    c={'gray'}
                                    aria-label={allSectionsExpanded ? 'Collapse all sections' : 'Expand all sections'}
                                    aria-expanded={allSectionsExpanded}
                                    aria-busy={expandingAll}
                                    onClick={handleToggleAllSections}
                                >
                                    {
                                        expandingAll ?
                                            <Loader size={12} color={'gray'} /> :
                                            <IconChevronDown className={styles.ease + ' ' + (!allSectionsExpanded ? styles.rotate90 : '')} size={'1em'} />
                                    }
                                </ActionIcon>
                            </Tooltip>
                        }
                        {
                            props.itemsSelected ? <>
                                <Checkbox
                                    size={'xs'}
                                    mx={'auto'}
                                    ml={10}
                                    color={'scBlue'}
                                    checked={checked}
                                    indeterminate={indeterminate}
                                    onChange={onSelectAllItems}
                                    icon={
                                        ({ indeterminate, className }: any) =>
                                            indeterminate ? <IconMinus className={className} strokeWidth={4} /> : <IconCheck className={className} strokeWidth={4} />
                                    }
                                />
                            </> :
                                props.savingItem &&
                                <Flex align={'center'} h={'100%'} w={'100%'} justify={'center'}>
                                    <Tooltip label={'Saving Data...'} color={'scBlue'}>
                                        <IconDeviceFloppy color={'var(--mantine-color-yellow-7)'} size={17} />
                                    </Tooltip>
                                </Flex>
                        }
                        </Flex>
                    </Table.Th>
                }
                {
                    mapping.map(
                        (x: any, i: any) =>
                            <Table.Th key={'simp' + i + 'col'}
                            // width: Math.round(100 / mapping.length) + '%',
                            >
                                <Tooltip events={{ hover: true, focus: false, touch: true }} label={x.tooltip ?? x.label} color={'scBlue'} openDelay={1000}>
                                    <Text
                                        size={'sm'}
                                        lineClamp={1}
                                        fw={600}
                                        style={{
                                            wordWrap: 'break-word',  // Enables breaking words to wrap to the next line
                                            wordBreak: 'keep-all',   // Attempts to keep whole words on the same line unless space is unavailable
                                            whiteSpace: 'normal',    // Allows wrapping behavior for multi-line text
                                            textAlign: (x.alignRight || x.currencyValue) ? 'end' : 'start'
                                        }}
                                        pr={x.alignRight || x.currencyValue ? 13 : 0}
                                        c={props.stylingProps?.darkerText ? 'gray.9' : 'gray.7'}
                                    >
                                        {x.label}
                                    </Text>
                                </Tooltip>
                            </Table.Th>
                    )
                }
                <Table.Th
                    className={styles.actionCell + (props.history && props.history.length > 1 || props.savingItem ? ' ' + styles.backgroundTransparent60 : styles.backgroundTransparent)}
                >
                    <Flex align={'center'} justify={'center'}
                        h={21}
                    >
                        {
                        }
                        {
                            // REMOVED UNDO FOR NOW?  Why ?? :'(
                            // props.history && props.history.length > 1 && props.onUndo &&
                        }
                    </Flex>
                </Table.Th>
            </Table.Tr>
        </Table.Thead>
        <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            modifiers={dndModifiers}
        >
            <Table.Tbody
            >
                {
                    groupedData.map(
                        (group: any, i: any) => (
                            <SectionTableSection
                                key={(group.id + 'section') as string}
                                group={group}
                                index={i}
                                lastItem={i === groupedData.length - 1}
                                prevItemGroupId={groupedData[i - 1]?.id}
                                previousItemIsSection={groupedData[i - 1]?.isSection}
                                mapping={mapping}
                                canEdit={props.canEdit}
                                controls={props.controls}
                                sectionControls={props.sectionControls}
                                stylingProps={props.stylingProps}
                                tableActionStates={props.tableActionStates}
                                showControlsOnHover={props.showControlsOnHover}
                                tableItemInputMetadataByKeyName={props.tableItemInputMetadataByKeyName}
                                allowSections={props.allowSections}
                                lineClamp={props.lineClamp}
                                useHideLineItemsPdfSetting={props.useHideLineItemsPdfSetting}
                                useDisplaySubtotalsPdfSetting={props.useDisplaySubtotalsPdfSetting}
                                sectionHeaderDisplayValueFunction={props.sectionHeaderDisplayValueFunction}
                                headingSubtotal={group.subTotal}
                                expanded={group.isSection && isSectionExpanded(group.id)}
                                onSetExpanded={onSetSectionExpanded}
                                focusTitle={group.id === newSectionId}
                                useSelectMode={!!props.itemsSelected}
                                selectedItems={selectedItems}
                                itemsSelectedFilter={props.itemsSelectedFilter}
                                onInputChange={onInputChange}
                                onItemClicked={onItemClicked}
                                onAction={onAction}
                                onSectionItem={onSectionItem}
                                onCreateNewSection={onCreateNewSection}
                                onUpdateGroup={onUpdateGroup}
                                onClearGroup={onClearGroup}
                                onItemsSelected={onItemsSelected}
                            />
                        )
                    )
                }
            </Table.Tbody>
        </DndContext>
    </>)

    const table = (
        <Table
            className={
                styles.mantineTable +
                (props.stylingProps?.compact ? ' ' + styles.compact : '') +
                (props.stylingProps?.rows ? ' ' + styles.striped : '')
            }
            withRowBorders={!props.stylingProps?.rows}
        >
            {tableHeadAndBody}

            {
                !!props.addButton &&
                <Table.Tr className={styles.addRow} style={{ borderBottom: 0 }}>
                    {
                        // (!!props.onReorderIndex || !!props.onReorder) &&
                        <Table.Td />
                    }
                    <Table.Td colSpan={mapping.length - 1}>
                        {
                            props.addButton.customComponent ??
                            <Flex w={'100%'} c={'scBlue'} align={'center'} gap={3} onClick={props.addButton.callback} >
                                <IconPlus size={16} />
                                <Anchor size={'sm'}>
                                    {props.addButton.label}
                                </Anchor>
                            </Flex>
                        }
                    </Table.Td>
                    {
                        !!props.controls && props.controls.length !== 0 &&
                        <Table.Td />
                    }
                </Table.Tr>
            }

            {
                props.footerRow &&
                <Table.Tfoot>
                    <Table.Tr>
                        {
                            props.footerRow.map(
                                (x: any, i: any) =>
                                    <Table.Td key={'simp' + i + 'col'}
                                        style={{
                                            minWidth: Math.round(100 / mapping.length) + '%'
                                        }}
                                    >

                                        <Text
                                            size={'sm'}
                                            lineClamp={1}
                                            style={{ wordBreak: 'break-word' }}
                                            fw={500}
                                            c={'gray.7'}
                                        >
                                            {x}
                                        </Text>
                                    </Table.Td>
                            )
                        }
                    </Table.Tr>
                </Table.Tfoot>
            }
        </Table>
    )

    return <>
        <div className={styles.tableContainer}
            style={{
                overflow: 'auto',
                maxHeight: props.height ?? 430,
                minHeight: props.minHeight ?? 'auto',
                maxWidth: props.width ?? 'auto'
            }}
        >
            {
                (!!props.customChildren || props.columnConfigModelName || props.useDisplaySubtotalsPdfSetting || props.useHideLineItemsPdfSetting) &&
                <Flex align={'center'} direction={{base: 'row', md: 'row-reverse'}} justify={{base: 'start', md: 'end'}} mb={{ base: 0}} >
                    {/*{
                        <Alert display={{ base: 'none', md: 'block' }} color={'scBlue'} variant={'transparent'} icon={<Text c={'goldenrod'} fw={600} size={'10px'}>NEW</Text>}>
                            <Flex align={'center'} gap={5}><Text size={'sm'} mt={2} >Try our new Sections and Bundles when adding items below</Text></Flex>
                        </Alert>
                    }*/}

                    {
                        props.customChildren
                    }

                    {
                        props.columnConfigModelName &&
                        <ScColumnMappingButton
                            size={'sm'}
                            tableName={props.columnConfigModelName}
                            columnMappingModelName={props.columnConfigModelName}
                            mapping={props.mapping}
                            onColumnMappingLoaded={setUserColMappingConfig}
                        />
                    }

                    {
                        (props.useDisplaySubtotalsPdfSetting || props.useHideLineItemsPdfSetting)/* && showGlobalPdfSettingCheckboxes*/ &&
                        <Flex ml={{ base: 20/*, md: 'auto'*/ }} mr={'sm'} align={'center'} pos={'initial'}>
                            <Text size={'sm'} mt={3} fw={600} >PDF Options :</Text>
                            {
                                props.useHideLineItemsPdfSetting &&
                                <Tooltip
                                    events={{ hover: true, focus: true, touch: true }}
                                    label={<Text size={'xs'}>Show line items for Sections on PDF </Text>}
                                    color={'scBlue'}
                                    openDelay={1000}
                                    closeDelay={0}
                                >
                                    <div>
                                        <Checkbox
                                            mb={-3}
                                            size={'xs'}
                                            ml={10}
                                            checked={!globalHideLineItemsChecked}
                                            indeterminate={globalHideLineItemsIndeterminate}
                                            onChange={(e: any) => {
                                                handleGlobalHideLineItemsChange(!e.currentTarget.checked)
                                            }}
                                            label={<Text size={'sm'}>Show line items for Sections</Text>}
                                            styles={{
                                                label: { paddingLeft: 10 }
                                            }}
                                            labelPosition={'right'}
                                            disabled={!(props.canEdit ?? true)}
                                        />
                                    </div>
                                </Tooltip>
                            }
                            {
                                props.useDisplaySubtotalsPdfSetting &&
                                <Tooltip
                                    label={<Text size={'xs'}>Display subtotals on PDF </Text>}
                                    color={'scBlue'}
                                    openDelay={1000}
                                    closeDelay={0}
                                    events={{ hover: true, focus: true, touch: true }}
                                >
                                    <div>
                                        <Checkbox
                                            mb={-3}
                                            ml={'sm'}
                                            checked={globalDisplaySubtotalChecked}
                                            indeterminate={globalDisplaySubtotalIndeterminate}
                                            onChange={(e: any) => {
                                                handleGlobalDisplaySubtotalChange(e.currentTarget.checked)
                                            }}
                                            size={'xs'}
                                            styles={{
                                                label: { paddingLeft: 10 }
                                            }}
                                            label={<Text size={'sm'}>Display subtotals</Text>}
                                            labelPosition={'right'}
                                            disabled={!(props.canEdit ?? true) || (globalHideLineItemsChecked && !globalHideLineItemsIndeterminate)}
                                        />
                                    </div>
                                </Tooltip>
                            }
                        </Flex>
                    }

                </Flex>
            }

            {table}

        </div>

    </>
}

export default SectionTable
